import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hooks as commonHooks } from '@sitebuilder/common';
import { types as coreTypes } from '@sitebuilder/core';
import { actionCreators } from '../../reducers/resources';
import { StateShape as ReduxStateShape } from '../../reducers';
import { filterIconSvg, filterIconActiveSvg } from '../../assets/images';

export interface ResourcesTableProps {
    resources: coreTypes.Resource | null;
    resourcesLoading: boolean;
    resourcesLoadError: any | null;
    resourcesType: string;
    fullWidth?: boolean;
    tableResources: coreTypes.Resource | null;
    setTableResources: (tableResources: coreTypes.Resource | null) => void;
}

const ResourcesTable = (props: ResourcesTableProps) => {
    const dispatch = useDispatch();
    const i18n = commonHooks.useLocalization();
    const { resources, resourcesLoading, resourcesLoadError, tableResources, setTableResources } =
        props;

    const locales: string[] = tableResources ? Object.keys(tableResources) : [];
    const [showSearchInput, setShowSearchInput] = useState<string>('');
    const [selectedResource, setSelectedResource] = useState<any>(null);

    const { resourcesSaveSuccess } = useSelector((state: ReduxStateShape) => ({
        ...state?.resources,
    }));

    useEffect(() => {
        if (resourcesSaveSuccess) {
            if (props.resourcesType === 'site') {
                dispatch(actionCreators.getResources());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourcesSaveSuccess]);

    const onClickResource = (id: string, value: string, locale: string) => {
        setSelectedResource({ id, value, locale });
    };

    const onFilterResources = (event: any, locale: string) => {
        const searchTerm = event.target.value;
        const filteredKeys: string[] = [];

        if (searchTerm === '') {
            setTableResources(resources);
        } else {
            if (resources && resources[locale]) {
                for (const key in resources[locale]) {
                    if (
                        resources?.[locale]?.[key]
                            ?.toLowerCase()
                            .includes(searchTerm?.toLowerCase())
                    ) {
                        filteredKeys.push(key);
                    }
                }
            }

            const searchResult =
                resources &&
                Object.keys(resources).reduce((result: any, locale: string) => {
                    result[locale] =
                        filteredKeys.length === 0
                            ? {}
                            : filteredKeys.reduce((obj: any, key: string) => {
                                  obj[key] = resources[locale][key];
                                  return obj;
                              }, {});
                    return result;
                }, {});

            setTableResources(searchResult);
        }
    };

    return (
        <div className="resources-section">
            <div className={`resources-table-container ${props.fullWidth ? 'full-width' : ''}`}>
                {resourcesLoading && !resources ? (
                    <div className={'loading'}>Loading...</div>
                ) : (
                    <>
                        {resources && !resourcesLoadError ? (
                            <table className="resources-table">
                                <thead>
                                    <tr>
                                        <th>{i18n.t('id')}</th>
                                        {locales?.map((locale: string) => (
                                            <th key={locale} id={locale}>
                                                {showSearchInput === locale ? (
                                                    <>
                                                        <input
                                                            type="text"
                                                            onChange={(e) =>
                                                                onFilterResources(e, locale)
                                                            }
                                                            placeholder={'Filter by value'}
                                                            data-testid={`custom-input-${locale}`}
                                                            className="custom-input"
                                                        />
                                                        <img
                                                            src={filterIconActiveSvg}
                                                            alt="Filter Resources"
                                                            onClick={() => setShowSearchInput('')}
                                                            className={'active-filter'}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>{locale}</span>
                                                        <img
                                                            src={filterIconSvg}
                                                            alt="Filter Resources"
                                                            onClick={() =>
                                                                setShowSearchInput(locale)
                                                            }
                                                            className={'filter'}
                                                            data-testid={`showInput-${locale}`}
                                                        />
                                                    </>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody id="resourcesTable" data-testid="resourceTest">
                                    {tableResources &&
                                        tableResources['en-US'] &&
                                        Object.keys(tableResources['en-US'])?.map((id: string) => (
                                            <tr key={id}>
                                                <td id={id}>{id}</td>
                                                {locales?.map((locale: string) => (
                                                    <td
                                                        key={locale}
                                                        className={`resource-value ${locale} ${
                                                            selectedResource?.id === id &&
                                                            selectedResource?.locale === locale
                                                                ? 'selected-value'
                                                                : ''
                                                        }`}
                                                        onClick={() => {
                                                            onClickResource(
                                                                id,
                                                                tableResources[locale][id],
                                                                locale
                                                            );
                                                        }}
                                                    >
                                                        {selectedResource?.id === id &&
                                                        selectedResource?.locale === locale ? (
                                                            <input
                                                                className="resource-input"
                                                                value={tableResources[locale][id]}
                                                                onChange={(e) =>
                                                                    setTableResources({
                                                                        ...tableResources,
                                                                        [locale]: {
                                                                            ...tableResources[
                                                                                locale
                                                                            ],
                                                                            [id]: e.target.value,
                                                                        },
                                                                    })
                                                                }
                                                            />
                                                        ) : (
                                                            tableResources[locale][id]
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={'error'}>
                                {!resources
                                    ? i18n.t('no_resources_found')
                                    : i18n.t('resources_load_error')}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ResourcesTable;
